// Deterministic replays of the recorded eval runs behind Lesson 3.8 ("Change One Thing and
// Run It Again"). Every number comes from runs made on 8 October 2026: three versions of the
// task-suggester prompt over the thirty shared notes, one temperature trial, one ceiling trial
// and the latency log. The graphics replay those records; they never call a model.

export type Tone = 'neutral' | 'success' | 'warning';
export interface Summary {
  message: string;
  tone: Tone;
}

// --- the thirty notes and the three prompt versions ---------------------------------------

export const NOTE_COUNT = 30;
/** The note titles in id order, as the eval set records them. */
export const NOTE_TITLES: readonly string[] = [
  'Apollo standup',
  'Venue email',
  'Thank-you note',
  'Planning call reminder',
  'Long planning note',
  'Catering note',
  'Support standup',
  'Chat with Ravi',
  'Partner email',
  'Retrospective',
  'Monday agenda',
  'Status update',
  'Backup room',
  'Questions answered',
  'Overdue invoice',
  'Two projects',
  'Furniture order',
  'Vague wish',
  'Note to self',
  'Two words',
  'Demo day',
  'Double booking',
  'Chat with Jo',
  'Ticket 311',
  'Standup round',
  'Partner FAQ draft',
  'Needs list',
  'Contract email',
  'Support rota',
  'All done',
];
export const noteTitle = (id: number): string => NOTE_TITLES[id - 1] ?? `Note ${id}`;

export const PROMPT_VERSIONS = [1, 2, 3] as const;
export type PromptVersion = (typeof PROMPT_VERSIONS)[number];
export const toPromptVersion = (value: string): PromptVersion =>
  PROMPT_VERSIONS.find((version) => String(version) === value) ?? 1;
export const promptVersionName = (version: PromptVersion) => `suggester-v${version}`;

/** The notes each version's first run failed, with the grader's first reason. */
export const FIRST_RUN_FAILURES: Record<PromptVersion, Readonly<Record<number, string>>> = {
  1: {
    5: 'missing: Check the dates in the release note once the venue sends the final schedule',
    8: 'missing: Send the invoice address to the venue',
    18: 'extra: Think about accessibility',
    19: 'missing: Send the final headcount of 58 to Priya today',
    25: "missing: Review Ravi's release note",
    26: 'extra: Send the parking map',
  },
  2: {
    15: 'extra: a problem with it',
    22: 'missing: Decide between the 13th and the library room and tell Priya by tomorrow',
    26: 'extra: Jo sends the map',
  },
  3: {
    19: 'missing: Send the final headcount of 58 to Priya today',
    26: 'extra: Jo sends the map',
  },
};
export const failedIds = (version: PromptVersion): number[] =>
  Object.keys(FIRST_RUN_FAILURES[version]).map(Number);
export const passCount = (version: PromptVersion) => NOTE_COUNT - failedIds(version).length;

// --- experiment-compare: what did the new prompt fix, and what did it break? --------------

export const COMPARISONS = ['1-2', '2-3'] as const;
export type Comparison = (typeof COMPARISONS)[number];
export const comparisonLabels: Record<Comparison, string> = {
  '1-2': 'Version 1 and version 2',
  '2-3': 'Version 2 and version 3',
};
export const toComparison = (value: string): Comparison =>
  COMPARISONS.find((comparison) => comparison === value) ?? '1-2';
export const comparisonVersions = (
  comparison: Comparison,
): readonly [PromptVersion, PromptVersion] => (comparison === '1-2' ? [1, 2] : [2, 3]);
export const COMPARE_GROUPS = ['all', 'fixed', 'broken', 'still'] as const;
export type CompareGroup = (typeof COMPARE_GROUPS)[number];
export const compareGroupLabels: Record<CompareGroup, string> = {
  all: 'All notes',
  fixed: 'Fixed',
  broken: 'Broken',
  still: 'Still failing',
};
export const toCompareGroup = (value: string): CompareGroup =>
  COMPARE_GROUPS.find((group) => group === value) ?? 'all';
export type Verdict = 'pass' | 'fail';
/** How one note moved between the two runs; `kept` means it passed both times. */
export type Change = 'fixed' | 'broken' | 'still' | 'kept';
export const changeLabels: Record<Change, string> = {
  fixed: 'fixed',
  broken: 'broken',
  still: 'still failing',
  kept: '',
};
export const changeOf = (failedBefore: boolean, failedAfter: boolean): Change =>
  failedBefore ? (failedAfter ? 'still' : 'fixed') : failedAfter ? 'broken' : 'kept';
export interface CompareRow {
  id: number;
  title: string;
  before: Verdict;
  after: Verdict;
  change: Change;
  /** The after run's first reason when it failed; empty when it passed. */
  reason: string;
}
export function compareRows(comparison: Comparison): CompareRow[] {
  const [before, after] = comparisonVersions(comparison);
  return NOTE_TITLES.map((title, index) => {
    const id = index + 1;
    const failedBefore = id in FIRST_RUN_FAILURES[before];
    const reason = FIRST_RUN_FAILURES[after][id] ?? '';
    const failedAfter = reason !== '';
    return {
      id,
      title,
      before: failedBefore ? 'fail' : 'pass',
      after: failedAfter ? 'fail' : 'pass',
      change: changeOf(failedBefore, failedAfter),
      reason,
    };
  });
}
export const changeIds = (comparison: Comparison, change: Change): number[] =>
  compareRows(comparison)
    .filter((row) => row.change === change)
    .map((row) => row.id);
export interface CompareState {
  comparison: Comparison;
  group: CompareGroup;
}
export const initialCompare = (): CompareState => ({ comparison: '1-2', group: 'all' });
export const groupRows = (rows: CompareRow[], group: CompareGroup): CompareRow[] =>
  group === 'all' ? rows : rows.filter((row) => row.change === group);
export const filteredCompareRows = (state: CompareState): CompareRow[] =>
  groupRows(compareRows(state.comparison), state.group);
const COUNT_WORDS: readonly string[] = [
  'no',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
];
export const countWord = (count: number): string => COUNT_WORDS[count] ?? String(count);
export function compareTotals(comparison: Comparison): string {
  const [before, after] = comparisonVersions(comparison);
  const count = (change: Change) => countWord(changeIds(comparison, change).length);
  return `${passCount(before)} of ${NOTE_COUNT} → ${passCount(after)} of ${NOTE_COUNT}: ${count('fixed')} fixed, ${count('broken')} broken, ${count('still')} still failing.`;
}
export const EMPTY_GROUP = 'No notes in this group.';
export function compareSummary(state: CompareState): Summary {
  return state.comparison === '1-2'
    ? {
        tone: 'warning',
        message: 'The count rose by three but hid two broken notes.',
      }
    : {
        tone: 'neutral',
        message: 'Note 19 flipped back with no change aimed at it: one note is noise.',
      };
}

// --- experiment-regression: why a rule about conditions broke the invoice note -----------

export const REGRESSION_NOTE_IDS = [15, 22] as const;
export type RegressionNoteId = (typeof REGRESSION_NOTE_IDS)[number];
export const toRegressionNoteId = (value: string): RegressionNoteId =>
  REGRESSION_NOTE_IDS.find((id) => String(id) === value) ?? 15;
export const TITLE_LIMITS = [80, 160] as const;
export type TitleLimit = (typeof TITLE_LIMITS)[number];
export const toTitleLimit = (value: string): TitleLimit =>
  TITLE_LIMITS.find((limit) => String(limit) === value) ?? 80;
/** The tasks table's own rule, which no prompt setting changes. */
export const TABLE_TITLE_LIMIT = 80;
export interface RegressionState {
  note: RegressionNoteId;
  /** The version 2 rule: keep the condition in the title. */
  keepCondition: boolean;
  limit: TitleLimit;
  /** The version 3 sentence: shorten, never split. */
  shorten: boolean;
}
export const initialRegression = (): RegressionState => ({
  note: 15,
  keepCondition: false,
  limit: 80,
  shorten: false,
});
/** Which recorded reply the controls select. With the rule off, nothing else matters. */
export type RegressionVariant = 'version-1' | 'split' | 'long' | 'version-3';
export function regressionVariant(state: RegressionState): RegressionVariant {
  if (!state.keepCondition) return 'version-1';
  if (state.shorten) return 'version-3';
  return state.limit === 160 ? 'long' : 'split';
}
export interface RecordedReply {
  titles: readonly string[];
  reasons: readonly string[];
}
/** The replies recorded for notes 15 and 22 under each combination of the rules. */
export const REGRESSION_REPLIES: Record<
  RegressionNoteId,
  Record<RegressionVariant, RecordedReply>
> = {
  15: {
    'version-1': {
      titles: ['Arrange payment for invoice 1042 or report any issues with it'],
      reasons: [],
    },
    split: {
      titles: [
        'Arrange payment for invoice 1042 or tell Accounts at Riverside Hall if there is ',
        'a problem with it',
      ],
      reasons: ['extra: a problem with it'],
    },
    long: {
      titles: [
        'Arrange payment for invoice 1042 or tell Accounts at Riverside Hall if there is a problem with it',
      ],
      reasons: [],
    },
    'version-3': {
      titles: ['Arrange payment for invoice 1042 or report if there is a problem with it'],
      reasons: [],
    },
  },
  22: {
    'version-1': {
      titles: [
        'Decide on alternative venue or date by tomorrow and inform Priya',
        'Notify all registered attendees if the date is moved',
      ],
      reasons: [],
    },
    split: {
      titles: [
        'Decide by tomorrow between the 13th or the main room at the library and tell Pri',
        'If we move, notify everyone who registered of the new date',
      ],
      reasons: [
        'missing: Decide between the 13th and the library room and tell Priya by tomorrow',
        'extra: Decide by tomorrow between the 13th or the main room at the library and tell Pri',
      ],
    },
    long: {
      titles: [
        'Decide by tomorrow between the 13th or the main room at the library and tell Priya',
        'If we move, notify everyone who registered of the new date',
      ],
      reasons: [],
    },
    'version-3': {
      titles: [
        'Decide by tomorrow and tell Priya',
        'If we move, notify everyone who registered of the new date',
      ],
      reasons: [],
    },
  },
};
export interface TitleRow {
  title: string;
  length: number;
  limit: TitleLimit;
  /** The title used every character the limit allowed. */
  atLimit: boolean;
}
export const TABLE_RULE_WARNING = 'The tasks table still refuses titles over 80 characters.';
export interface RegressionOutcome {
  variant: RegressionVariant;
  titles: TitleRow[];
  passed: boolean;
  reasons: readonly string[];
  /** Set when the grader passes a title the tasks table would refuse. */
  warning: string | null;
}
export function regressionOutcome(state: RegressionState): RegressionOutcome {
  const variant = regressionVariant(state);
  const reply = REGRESSION_REPLIES[state.note][variant];
  const titles = reply.titles.map((title) => ({
    title,
    length: title.length,
    limit: state.limit,
    atLimit: title.length >= state.limit,
  }));
  const overTableRule = titles.some((row) => row.length > TABLE_TITLE_LIMIT);
  return {
    variant,
    titles,
    passed: reply.reasons.length === 0,
    reasons: reply.reasons,
    warning: overTableRule ? TABLE_RULE_WARNING : null,
  };
}
export const charactersText = (row: TitleRow) =>
  `${row.length} of ${row.limit}${row.atLimit ? ' — at the limit' : ''}`;
/** The Grader panel: the verdict, then each reason on its own line. */
export const graderText = (outcome: RegressionOutcome) =>
  outcome.passed ? 'pass' : ['fail', ...outcome.reasons].join('\n');
export const onOff = (value: boolean) => (value ? 'on' : 'off');
export function regressionSummary(state: RegressionState): Summary {
  const outcome = regressionOutcome(state);
  switch (outcome.variant) {
    case 'version-1':
      return { tone: 'neutral', message: 'Version 1: short titles, no condition kept.' };
    case 'split':
      return {
        tone: 'warning',
        message: 'The longer title hit the 80-character limit, and the rest became a second title.',
      };
    case 'long': {
      const longest = Math.max(...outcome.titles.map((row) => row.length));
      return {
        tone: 'warning',
        message: `The grader passes a ${longest}-character title at 160. ${TABLE_RULE_WARNING}`,
      };
    }
    case 'version-3':
      return {
        tone: 'success',
        message: 'With the version 3 rule the title is shortened, not split.',
      };
  }
}

// --- experiment-settings: which setting changes the result, and which the bill? ----------

export const TEMPERATURES = [0, 1] as const;
export type Temperature = (typeof TEMPERATURES)[number];
export const toTemperature = (value: string): Temperature =>
  TEMPERATURES.find((temperature) => String(temperature) === value) ?? 0;
export const CEILINGS = [400, 60] as const;
export type Ceiling = (typeof CEILINGS)[number];
export const toCeiling = (value: string): Ceiling =>
  CEILINGS.find((ceiling) => String(ceiling) === value) ?? 400;
export const MODEL_NAME = 'zai.glm-5';
export const MODEL_LINE = `Model: ${MODEL_NAME} (kept fixed in these three lessons)`;
/** The example rate: 3.00 per million input tokens, 15.00 per million output tokens. */
export const EXAMPLE_INPUT_RATE = 3;
export const EXAMPLE_OUTPUT_RATE = 15;
export const exampleCost = (promptTokens: number, completionTokens: number): number =>
  (promptTokens * EXAMPLE_INPUT_RATE + completionTokens * EXAMPLE_OUTPUT_RATE) / 1_000_000;
export const costText = (cost: number) => cost.toFixed(6);
export interface SettingsState {
  version: PromptVersion;
  temperature: Temperature;
  ceiling: Ceiling;
}
export const initialSettings = (): SettingsState => ({ version: 1, temperature: 0, ceiling: 400 });
export interface RecordedRun {
  passed: number;
  of: number;
  promptTokens: number;
  completionTokens: number;
}
/** One full run per version at temperature 0 with a 400-token ceiling. */
export const FULL_RUNS: Record<PromptVersion, RecordedRun> = {
  1: { passed: 24, of: 30, promptTokens: 4884, completionTokens: 655 },
  2: { passed: 27, of: 30, promptTokens: 7494, completionTokens: 738 },
  3: { passed: 28, of: 30, promptTokens: 8094, completionTokens: 699 },
};
/** Version 3 run once more at temperature 1: the same verdicts, nine more completion tokens. */
export const TEMPERATURE_ONE_RUN: RecordedRun = {
  passed: 28,
  of: 30,
  promptTokens: 8094,
  completionTokens: 708,
};
/** Version 3 with max_tokens 60 on four notes with long replies: nothing reached the ceiling. */
export const CEILING_TRIAL = {
  version: 3,
  notes: [5, 15, 22, 27],
  passed: 4,
  promptTokens: 1277,
  completionTokens: 163,
} as const;
/** The recorded full run for these settings, or null when none was made. */
export function recordedRun(state: SettingsState): RecordedRun | null {
  if (state.ceiling === 60) return null;
  if (state.temperature === 0) return FULL_RUNS[state.version];
  return state.version === 3 ? TEMPERATURE_ONE_RUN : null;
}
export const promptTokensPerCall = (version: PromptVersion) =>
  Math.round(FULL_RUNS[version].promptTokens / NOTE_COUNT);
export const completionTokensPerCall = (version: PromptVersion) =>
  Math.round(FULL_RUNS[version].completionTokens / NOTE_COUNT);
export const runCost = (version: PromptVersion) =>
  exampleCost(FULL_RUNS[version].promptTokens, FULL_RUNS[version].completionTokens);
/** How much dearer one 30-note run became between two versions, in whole percent. */
export const billRisePercent = (from: PromptVersion, to: PromptVersion) =>
  Math.round((runCost(to) / runCost(from) - 1) * 100);
export const NOT_MEASURED = 'not measured';
export interface SettingsReading {
  passLine: string;
  promptTokens: number;
  costLine: string;
}
export function settingsReading(state: SettingsState): SettingsReading {
  const run = recordedRun(state);
  const trial = state.ceiling === 60 && state.version === CEILING_TRIAL.version;
  return {
    passLine: run
      ? `${run.passed} of ${run.of}`
      : trial
        ? `${CEILING_TRIAL.passed} of ${CEILING_TRIAL.notes.length}, no full run`
        : NOT_MEASURED,
    promptTokens: promptTokensPerCall(state.version),
    costLine: run
      ? `${costText(exampleCost(run.promptTokens, run.completionTokens))} (example rate)`
      : trial
        ? `${costText(exampleCost(CEILING_TRIAL.promptTokens, CEILING_TRIAL.completionTokens))} for ${CEILING_TRIAL.notes.length} notes (example rate)`
        : NOT_MEASURED,
  };
}
/** The Settings panel: the fixed model line, then the three settings as the code names them. */
export const settingsText = (state: SettingsState) =>
  [
    MODEL_LINE,
    `prompt_version: ${promptVersionName(state.version)}`,
    `temperature: ${state.temperature}`,
    `max_tokens: ${state.ceiling}`,
  ].join('\n');
export const CEILING_MESSAGE =
  'A ceiling is a safety stop, not a saving: 60 cut nothing on the notes tried.';
export const NOT_MEASURED_MESSAGE =
  'Not measured. A run costs about 0.03, so run it and write the result down.';
export function settingsSummary(state: SettingsState): Summary {
  if (state.ceiling === 60) return { tone: 'neutral', message: CEILING_MESSAGE };
  if (state.temperature === 1)
    return state.version === 3
      ? {
          tone: 'neutral',
          message: 'Temperature 1 changed no verdict in one run and no cost at all.',
        }
      : { tone: 'neutral', message: NOT_MEASURED_MESSAGE };
  if (state.version === 1)
    return {
      tone: 'neutral',
      message: `Version 1: ${passCount(1)} of ${NOTE_COUNT} for ${costText(runCost(1))}. Change one setting and watch which number moves.`,
    };
  return {
    tone: 'warning',
    message: `The prompt grew from ${promptTokensPerCall(1)} to ${promptTokensPerCall(state.version)} tokens per call, and the bill rose ${billRisePercent(1, state.version)} percent.`,
  };
}

// --- experiment-latency: what one run costs in time, and which number to quote -----------

/** latency_ms per call from the log, in note order: run 1 then run 2 for versions 1 and 2. */
export const LATENCIES: Record<PromptVersion, readonly number[]> = {
  1: [
    1915, 1123, 630, 3152, 1006, 714, 993, 4331, 1143, 1773, 861, 709, 816, 721, 677, 743, 853,
    5037, 670, 815, 712, 874, 854, 991, 647, 861, 1411, 885, 681, 2943, 2050, 2459, 852, 859, 883,
    722, 3745, 910, 632, 796, 972, 1041, 1774, 617, 7262, 876, 1089, 981, 1727, 690, 642, 1369, 922,
    918, 626, 629, 896, 911, 716, 622,
  ],
  2: [
    946, 849, 649, 757, 874, 686, 812, 964, 1062, 1386, 662, 658, 728, 606, 1150, 828, 9373, 601,
    729, 4980, 794, 1107, 933, 803, 633, 777, 2331, 1153, 679, 877, 1011, 818, 7050, 660, 922, 4816,
    750, 896, 1123, 5945, 649, 742, 1408, 602, 980, 1092, 3716, 644, 994, 652, 1823, 3869, 707,
    1041, 771, 812, 832, 802, 854, 777,
  ],
  3: [
    1429, 1034, 946, 4630, 874, 800, 865, 848, 950, 829, 763, 707, 770, 2074, 858, 893, 2116, 3932,
    4374, 803, 677, 3367, 14365, 934, 647, 704, 4608, 1183, 1272, 1111,
  ],
};
export const latencyVersionLabels: Record<PromptVersion, string> = {
  1: `Version 1 (${LATENCIES[1].length} calls)`,
  2: `Version 2 (${LATENCIES[2].length} calls)`,
  3: `Version 3 (${LATENCIES[3].length} calls)`,
};
export const STATISTICS = ['mean', 'p50', 'p95', 'max'] as const;
export type Statistic = (typeof STATISTICS)[number];
export const statisticLabels: Record<Statistic, string> = {
  mean: 'Mean',
  p50: 'p50',
  p95: 'p95',
  max: 'Maximum',
};
export const toStatistic = (value: string): Statistic =>
  STATISTICS.find((statistic) => statistic === value) ?? 'mean';
export interface LatencyState {
  version: PromptVersion;
  statistic: Statistic;
}
export const initialLatency = (): LatencyState => ({ version: 1, statistic: 'mean' });
export const sortAscending = (values: readonly number[]): number[] =>
  [...values].sort((a, b) => a - b);
/** PostgreSQL's percentile_cont: sort, take position (n − 1)·p, interpolate between neighbours. */
export function percentileCont(values: readonly number[], fraction: number): number {
  const sorted = sortAscending(values);
  if (sorted.length === 0) return 0;
  const position = (sorted.length - 1) * fraction;
  const lower = Math.floor(position);
  const low = sorted[lower] ?? 0;
  const high = sorted[Math.min(lower + 1, sorted.length - 1)] ?? low;
  return low + (high - low) * (position - lower);
}
export const meanOf = (values: readonly number[]): number =>
  values.length === 0 ? 0 : values.reduce((sum, value) => sum + value, 0) / values.length;
/** PostgreSQL's round() on a double sends a half to the even neighbour: 840.5 prints as 840. */
export function roundLikePostgres(value: number): number {
  const floor = Math.floor(value);
  const rest = value - floor;
  if (rest > 0.5) return floor + 1;
  if (rest < 0.5) return floor;
  return floor % 2 === 0 ? floor : floor + 1;
}
export interface LatencyStats {
  calls: number;
  mean: number;
  p50: number;
  p95: number;
  max: number;
}
export function latencyStats(version: PromptVersion): LatencyStats {
  const values = LATENCIES[version];
  return {
    calls: values.length,
    mean: roundLikePostgres(meanOf(values)),
    p50: roundLikePostgres(percentileCont(values, 0.5)),
    p95: roundLikePostgres(percentileCont(values, 0.95)),
    max: Math.max(...values),
  };
}
export const statisticValue = (stats: LatencyStats, statistic: Statistic): number =>
  stats[statistic];
export const LATENCY_SQL =
  "SELECT prompt_version, count(*) AS calls, round(percentile_cont(0.5) WITHIN GROUP (ORDER BY latency_ms)) AS p50_ms, round(percentile_cont(0.95) WITHIN GROUP (ORDER BY latency_ms)) AS p95_ms FROM model_calls WHERE source = 'eval' GROUP BY prompt_version ORDER BY prompt_version";
export interface LatencyBar {
  value: number;
  /** Height as a percentage of the slowest call of the selected version. */
  height: number;
  /** The first sorted call at or above the selected statistic carries the mark. */
  marked: boolean;
}
export function latencyBars(state: LatencyState): LatencyBar[] {
  const sorted = sortAscending(LATENCIES[state.version]);
  const stats = latencyStats(state.version);
  const value = statisticValue(stats, state.statistic);
  const markedIndex = sorted.findIndex((latency) => latency >= value);
  return sorted.map((latency, index) => ({
    value: latency,
    height: Math.round((latency / stats.max) * 100),
    marked: index === markedIndex,
  }));
}
/** Where the marker line sits, as a percentage of the strip's height. */
export const markHeight = (state: LatencyState): number => {
  const stats = latencyStats(state.version);
  return Math.round((statisticValue(stats, state.statistic) / stats.max) * 100);
};
export function statisticLine(state: LatencyState): string {
  const stats = latencyStats(state.version);
  const value = statisticValue(stats, state.statistic);
  switch (state.statistic) {
    case 'mean':
      return `Mean: ${value} ms (the sum of ${stats.calls} latencies divided by ${stats.calls})`;
    case 'p50':
      return `p50: ${value} ms (percentile_cont(0.5) over ${stats.calls} sorted calls)`;
    case 'p95':
      return `p95: ${value} ms (percentile_cont(0.95) over ${stats.calls} sorted calls)`;
    case 'max':
      return `Maximum: ${value} ms (the slowest of ${stats.calls} calls)`;
  }
}
export function latencySummary(state: LatencyState): Summary {
  const stats = latencyStats(state.version);
  switch (state.statistic) {
    case 'mean':
      return {
        tone: 'warning',
        message: `The mean is pulled up by one call of ${stats.max} ms.`,
      };
    case 'p50':
      return {
        tone: 'success',
        message: `Half the calls finished within ${stats.p50} ms: what a typical call costs.`,
      };
    case 'p95':
      return {
        tone: 'success',
        message: `One call in twenty took longer than ${stats.p95} ms. Quote p50 and p95 together.`,
      };
    case 'max':
      return {
        tone: 'neutral',
        message: 'The slowest call. Useful to know, useless to quote alone.',
      };
  }
}
